import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { QrCode, X, Download } from "lucide-react";

/**
 * Admin-only navbar shortcut. Renders a small QR icon button that opens a
 * full-screen presentation modal with the admin's TBC referral QR
 * (`/join?ref=TBC-XXXXXX`). Same code the "My Cards" page uses — this is
 * just a persistent one-tap version pinned to the top nav so Matt can hand
 * his phone to anyone from any page.
 *
 * Ref code is fetched once and cached across mounts.
 */

let _cachedRef = null;
let _cachedPromise = null;

async function fetchRef() {
  if (_cachedRef) return _cachedRef;
  if (_cachedPromise) return _cachedPromise;
  _cachedPromise = axios
    .get(`${API}/referrals/me`, { withCredentials: true })
    .then((r) => {
      _cachedRef = r.data || { code: "", share_url: "" };
      return _cachedRef;
    })
    .catch(() => {
      _cachedRef = { code: "", share_url: "" };
      return _cachedRef;
    })
    .finally(() => { _cachedPromise = null; });
  return _cachedPromise;
}

export default function AdminQuickQRButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState(null);
  const canvasWrapRef = useRef(null);

  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    if (!isAdmin) return;
    if (ref) return;
    fetchRef().then(setRef);
  }, [isAdmin, ref]);

  // Escape closes the fullscreen presenter
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const download = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `mtg-referral-qr.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const copyLink = async () => {
    if (!ref?.share_url) return;
    try {
      await navigator.clipboard.writeText(ref.share_url);
      toast.success("Referral link copied.");
    } catch {
      toast.error("Clipboard blocked — long-press the URL to copy.");
    }
  };

  if (!isAdmin) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-tbc-gold/60 bg-tbc-gold/10 hover:bg-tbc-gold/20 text-tbc-navy text-xs font-semibold transition-colors"
        title="Show your Midas Touch referral QR"
        data-testid="admin-quick-qr-btn"
      >
        <QrCode className="w-3.5 h-3.5" />
        My QR
      </button>
      {/* Mobile-compact variant (icon-only). Keeps the nav clean on small screens. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="sm:hidden p-2 rounded-full text-tbc-navy hover:bg-gray-100"
        title="Show your Midas Touch referral QR"
        data-testid="admin-quick-qr-btn-mobile"
      >
        <QrCode className="w-5 h-5" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[150] bg-tbc-navy flex flex-col items-center justify-center px-6 py-10"
          data-testid="admin-quick-qr-fullscreen"
        >
          <button
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Close"
            data-testid="admin-quick-qr-close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="w-full max-w-md flex flex-col items-center gap-6">
            <div className="text-center text-white">
              <p className="font-heading text-2xl sm:text-3xl text-tbc-gold">
                Midas Touch LLC
              </p>
              <p className="text-sm text-white/70 mt-1">
                Scan to join The Blk Community
                {ref?.code && (
                  <>
                    {" "}·{" "}
                    <span className="font-mono text-tbc-gold">{ref.code}</span>
                  </>
                )}
              </p>
            </div>

            <div
              ref={canvasWrapRef}
              className="p-6 bg-white rounded-3xl shadow-2xl"
              data-testid="admin-quick-qr-canvas"
            >
              {ref?.share_url ? (
                <QRCodeCanvas
                  value={ref.share_url}
                  size={320}
                  level="H"
                  includeMargin={false}
                  fgColor="#0A192F"
                  bgColor="#ffffff"
                />
              ) : (
                <div className="w-[320px] h-[320px] flex items-center justify-center text-gray-400 text-sm">
                  Generating…
                </div>
              )}
            </div>

            {ref?.share_url && (
              <p className="text-center text-xs text-white/60 font-mono break-all max-w-xs">
                {ref.share_url}
              </p>
            )}

            <div className="flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={copyLink}
                disabled={!ref?.share_url}
                className="inline-flex items-center px-4 py-2 rounded-md text-sm bg-white/10 border border-white/20 text-white hover:bg-white/20 disabled:opacity-50"
                data-testid="admin-quick-qr-copy"
              >
                Copy link
              </button>
              <button
                type="button"
                onClick={download}
                disabled={!ref?.share_url}
                className="inline-flex items-center px-4 py-2 rounded-md text-sm bg-white/10 border border-white/20 text-white hover:bg-white/20 disabled:opacity-50"
                data-testid="admin-quick-qr-download"
              >
                <Download className="w-4 h-4 mr-2" />
                Save PNG
              </button>
            </div>
          </div>

          <p className="absolute bottom-6 text-[10px] uppercase tracking-widest text-white/40">
            A Midas Touch Group LLC company
          </p>
        </div>
      )}
    </>
  );
}
